import { useFieldArray } from 'react-hook-form';
import { SelectField } from '@mews-commander/form';
import { FormInputField, LocalizedText, lt, useFeatureFlag } from '@mews/framework';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { Grid, Stack } from '@optimus-web/core';
import { useAccountingCategoriesOptions } from '../../hooks/useAccountingCategoriesOptions';
import { useCurrentEnterpriseTaxRateOptions } from '../../hooks/useCurrentEnterpriseTaxCodeOptions';
import { FieldNames } from '../fieldNames';
import { PaymentMappingsList } from '../paymentMappings/PaymentMappingList';
import { MicrosOutletFormSchema } from './formProps';

export interface MicrosOutletFormProps {
    isDetailedBillingEnabled?: boolean;
    isMultiDepartmentEnabled?: boolean;
}

export const MicrosOutletForm = ({ isDetailedBillingEnabled, isMultiDepartmentEnabled }: MicrosOutletFormProps) => {
    const posMicrosConfigurationOutletKeyEnabled = useFeatureFlag(FeatureFlag.posMicrosConfigurationOutletKeyEnabled);
    const { accountingCategoriesOptions } = useAccountingCategoriesOptions();
    const allTaxRates = useCurrentEnterpriseTaxRateOptions();
    const { fields } = useFieldArray<MicrosOutletFormSchema, 'netSalesAccountingCategories'>({
        name: FieldNames.netSalesAccountingCategories,
    });

    return (
        <Stack vertical spacing="400">
            <Stack vertical spacing="200">
                <FormInputField
                    name={FieldNames.name}
                    label={<LocalizedText textKey={TextKey.RevenueCenter} />}
                    required
                />
                <FormInputField
                    name={FieldNames.menuNumber}
                    label={<LocalizedText textKey={TextKey.MenuNumber} />}
                    required
                />
                {posMicrosConfigurationOutletKeyEnabled && (
                    <FormInputField
                        name={FieldNames.outletBillId}
                        label={<LocalizedText textKey={TextKey.MicrosOutletId} />}
                    />
                )}
                <FormInputField
                    name={FieldNames.serviceId}
                    label={<LocalizedText textKey={TextKey.ServiceKey} />}
                    required
                />
                {fields.map((field, index) => (
                    <Grid
                        key={field.id}
                        columnGap="200"
                        templateRows="repeat(auto-fit, minmax(0, 2fr), minmax(0, 2fr))"
                        templateColumns="minmax(0, 2fr) minmax(0, 2fr)"
                        height="100%"
                        tabletOnly={{
                            templateColumns: 'minmax(0, 4fr)',
                        }}
                        largePhoneOnly={{
                            templateColumns: 'minmax(0, 2fr)',
                        }}
                        phoneOnly={{
                            templateColumns: 'minmax(0, 1fr)',
                        }}
                    >
                        <SelectField
                            name={`${FieldNames.netSalesAccountingCategories}.${index}.${FieldNames.categoryId}`}
                            label={
                                <>
                                    {lt('NetSaleAccountingCategory')}
                                    <span>{` ${index + 1}`}</span>
                                </>
                            }
                            options={accountingCategoriesOptions}
                            showEmptyValue
                            disabled={index > 0 && !isMultiDepartmentEnabled}
                        />
                        <SelectField
                            name={`${FieldNames.netSalesAccountingCategories}.${index}.${FieldNames.taxCodes}`}
                            label={lt('TaxCodes')}
                            options={allTaxRates}
                            multiselect
                            disabled={index > 0 && !isMultiDepartmentEnabled}
                        />
                    </Grid>
                ))}
                <Grid
                    columnGap="200"
                    templateRows="repeat(auto-fit, minmax(0, 2fr), minmax(0, 2fr))"
                    templateColumns="minmax(0, 2fr) minmax(0, 2fr)"
                    height="100%"
                    tabletOnly={{
                        templateColumns: 'minmax(0, 4fr)',
                    }}
                    largePhoneOnly={{
                        templateColumns: 'minmax(0, 2fr)',
                    }}
                    phoneOnly={{
                        templateColumns: 'minmax(0, 1fr)',
                    }}
                >
                    <SelectField
                        name={FieldNames.discountAccountingCategoryId}
                        label={lt('DiscountAccountingCategory')}
                        disabled={!isDetailedBillingEnabled}
                        options={accountingCategoriesOptions}
                        showEmptyValue
                    />
                    <SelectField
                        name={FieldNames.discountAccountingCategoryTaxCodes}
                        label={lt('TaxCodes')}
                        multiselect
                        disabled={!isDetailedBillingEnabled}
                        options={allTaxRates}
                    />
                    <SelectField
                        name={FieldNames.tipsAccountingCategoryId}
                        label={lt('TipsAccountingCategory')}
                        disabled={!isDetailedBillingEnabled}
                        options={accountingCategoriesOptions}
                        showEmptyValue
                    />
                    <SelectField
                        name={FieldNames.tipsAccountingCategoryTaxCodes}
                        label={lt('TaxCodes')}
                        multiselect
                        disabled={!isDetailedBillingEnabled}
                        options={allTaxRates}
                    />
                    <SelectField
                        name={FieldNames.serviceChargeAccountingCategoryId}
                        label={lt('ServiceChargeAccountingCategory')}
                        disabled={!isDetailedBillingEnabled}
                        options={accountingCategoriesOptions}
                        showEmptyValue
                    />
                    <SelectField
                        name={FieldNames.serviceChargeAccountingCategoryTaxCodes}
                        label={lt('TaxCodes')}
                        multiselect
                        disabled={!isDetailedBillingEnabled}
                        options={allTaxRates}
                    />
                </Grid>
            </Stack>
            <PaymentMappingsList />
        </Stack>
    );
};
